import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Filter, CloudUpload, FileText, FileSpreadsheet, Lightbulb, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const ProcessingWorkflow = () => {
  // Fetch real transactions that need review
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ['transactions-review'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .or('needs_review.eq.true,ai_classification_confidence.lt.90')
        .order('transaction_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    }
  });

  const getStatusInfo = (transaction: any) => {
    const confidence = transaction.ai_classification_confidence || 0;
    if (transaction.needs_review) {
      return { label: 'Needs Review', color: 'bg-yellow-100 text-yellow-800' };
    }
    if (confidence >= 90) {
      return { label: 'Auto-Categorized', color: 'bg-blue-100 text-blue-800' };
    }
    if (confidence < 50) {
      return { label: 'Flagged', color: 'bg-red-100 text-red-800' };
    }
    return { label: 'Needs Review', color: 'bg-yellow-100 text-yellow-800' };
  };
  return (
    <section className="grid grid-cols-3 gap-6">
      {/* Transaction Review Queue */}
      <Card className="col-span-3 xl:col-span-2">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-bold text-brand-primary">
              Transaction Review Queue ({transactions.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Filter className="w-3 h-3 mr-1" />
                Filter
              </Button>
              <Button size="sm" className="h-7 text-xs bg-brand-primary hover:bg-brand-primary/90">
                Bulk Edit
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No transactions need review. Great work!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-secondary rounded">
                  <tr>
                    <th className="p-0 w-8">
                      <div className="w-4 h-4 border border-border rounded-sm m-auto" />
                    </th>
                    <th className="px-4 py-2 text-left text-xs text-muted-foreground font-medium tracking-wider">
                      TRANSACTION
                    </th>
                    <th className="px-4 py-2 text-left text-xs text-muted-foreground font-medium tracking-wider">
                      DATE
                    </th>
                    <th className="px-4 py-2 text-left text-xs text-muted-foreground font-medium tracking-wider">
                      AMOUNT (CHF)
                    </th>
                    <th className="px-4 py-2 text-left text-xs text-muted-foreground font-medium tracking-wider">
                      AI CONFIDENCE
                    </th>
                    <th className="px-4 py-2 text-left text-xs text-muted-foreground font-medium tracking-wider">
                      STATUS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => {
                    const status = getStatusInfo(transaction);
                    return (
                      <tr key={transaction.id} className={index < transactions.length - 1 ? "border-b border-border" : ""}>
                        <td className="p-0 w-8">
                          <div className="w-4 h-4 border border-border rounded-sm m-auto" />
                        </td>
                        <td className="px-4 py-3 text-xs font-medium">{transaction.description}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {new Date(transaction.transaction_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium">
                          {transaction.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <Progress value={transaction.ai_classification_confidence || 0} className="h-1.5" />
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={`text-xs ${status.color}`}>
                            {status.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right Column */}
      <div className="col-span-3 xl:col-span-1 flex flex-col gap-6">
        {/* Smart File Processor */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold text-brand-primary">Smart File Processor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:bg-brand-secondary/50 transition-colors">
              <CloudUpload className="w-8 h-8 text-brand-primary mb-2 mx-auto" />
              <p className="text-sm font-medium">Drag & Drop Files</p>
              <p className="text-xs text-muted-foreground">or click to browse</p>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-3 text-xs">
                <FileText className="w-4 h-4 text-red-500" />
                <span className="flex-grow">invoices_sept.pdf</span>
                <span className="text-muted-foreground">Processing...</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <FileSpreadsheet className="w-4 h-4 text-green-700" />
                <span className="flex-grow">expenses_q3.xlsx</span>
                <span className="text-green-700 font-medium">Done</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Learning & AI Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold text-brand-primary">AI Recommendations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3.5">
              <div className="w-8 h-8 mt-1 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs">
                  <span className="font-bold">Suggestion:</span> Create a rule for "SBB" transactions to auto-categorize as "Travel".
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3.5">
              <div className="w-8 h-8 mt-1 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                <Star className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs">
                  <span className="font-bold">Good Catch!</span> You correctly re-categorized 3 flagged transactions yesterday.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};